import { useMemo, useState } from 'react';
import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { gbp, count, faintIfNull, statusPill, NO_DATA } from '../lib/format.js';

interface Keyword {
  campaign: string; adGroup: string; text: string;
  matchType: string; status: string;
  qualityScore: number | null;
  hasServed: boolean;
  impressions: number | null; clicks: number | null; costGbp: number | null;
}

interface KeywordsResponse {
  window: { from: string; to: string; days: number };
  keywords: Keyword[];
  generatedUtc: string;
}

const ALL = '__all__';

export function Keywords({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error } = useApi<KeywordsResponse>('/api/keywords?days=30', getToken);
  const [campaign, setCampaign] = useState(ALL);
  const [matchType, setMatchType] = useState(ALL);

  const campaigns = useMemo(
    () => Array.from(new Set((data?.keywords ?? []).map((k) => k.campaign))).sort(),
    [data],
  );
  const matchTypes = useMemo(
    () => Array.from(new Set((data?.keywords ?? []).map((k) => k.matchType))).sort(),
    [data],
  );

  if (error) return <EmptyState title="Could not load keywords" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading keywords from the Growth Engine database.</EmptyState>;

  const rows = data.keywords.filter(
    (k) => (campaign === ALL || k.campaign === campaign) && (matchType === ALL || k.matchType === matchType),
  );
  const served = data.keywords.filter((k) => k.hasServed);
  const scored = data.keywords.filter((k) => k.qualityScore !== null);

  return (
    <div className="stack">
      {served.length === 0 ? (
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>No keyword has served</h2>
          <p>
            These {count(data.keywords.length)} keywords are live in the account but no campaign has run,
            so impressions, clicks and cost read <strong>{NO_DATA}</strong>. Quality Score is withheld by
            Google until a keyword accumulates traffic — an absent score is not a bad score, and is not
            shown as one.
          </p>
        </div>
      ) : null}

      <Panel title="Keyword coverage" subtitle="What we are bidding on, by campaign and match type.">
        <div className="kpi-grid">
          <CountKpi label="Keywords" value={data.keywords.length} />
          <CountKpi label="Campaigns" value={campaigns.length} />
          <CountKpi
            label="With Quality Score"
            value={scored.length > 0 ? scored.length : null}
            note="Google withholds it until there is traffic"
          />
          <CountKpi
            label="Keywords that have served"
            value={served.length > 0 ? served.length : null}
            note={`Window ${data.window.from} to ${data.window.to}`}
          />
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 18, flexWrap: 'wrap' }}>
          {matchTypes.map((m) => {
            const n = data.keywords.filter((k) => k.matchType === m).length;
            return (
              <div key={m} style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <span className="pill">{m}</span>{' '}
                <strong style={{ color: 'var(--text)' }}>{count(n)}</strong>
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Keywords" subtitle={`Performance window: ${data.window.from} to ${data.window.to}.`}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <select className="select" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
            <option value={ALL}>All campaigns</option>
            {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="select" value={matchType} onChange={(e) => setMatchType(e.target.value)}>
            <option value={ALL}>All match types</option>
            {matchTypes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--text-faint)' }}>
            {count(rows.length)} of {count(data.keywords.length)} keywords
          </span>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>Keyword</th>
              <th>Match</th>
              <th>Ad group</th>
              <th>Status</th>
              <th className="num">Quality Score</th>
              <th className="num">Impressions</th>
              <th className="num">Clicks</th>
              <th className="num">Spend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((k) => (
              <tr key={`${k.campaign}|${k.adGroup}|${k.text}|${k.matchType}`}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{k.text}</td>
                <td><span className="pill">{k.matchType}</span></td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{k.adGroup}</td>
                <td><span className={statusPill(k.status)}>{k.status}</span></td>
                <td className="num" style={faintIfNull(k.qualityScore)}>{count(k.qualityScore)}</td>
                <td className="num" style={faintIfNull(k.impressions)}>{count(k.impressions)}</td>
                <td className="num" style={faintIfNull(k.clicks)}>{count(k.clicks)}</td>
                <td className="num" style={faintIfNull(k.costGbp)}>{gbp(k.costGbp)}</td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr><td colSpan={8} style={{ color: 'var(--text-faint)' }}>No keywords match this filter.</td></tr>
            ) : null}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
